import {memo, useEffect, useState} from "react";
import {Button, Typography, Space} from "antd";
import {PlayCircleOutlined, PauseCircleOutlined} from "@ant-design/icons";
import PropTypes from "prop-types";
import ReactConfetti from "react-confetti";
import "./randomLottery.css";
import {useSlotReel} from "./useSlotReel";
import ReelWindow from "./ReelWindow";
import ParticipantMarquee from "./ParticipantMarquee";

const {Text, Title} = Typography;

const CONFETTI_DURATION_MS = 6000;

const RandomLotteryComponent = ({
                                    name = "Урамшуулал",
                                    allLotteries = [],
                                    winnableLotteries = [],
                                    meritWinner = null,
                                    spinDuration = 5,
                                    scrollSpeed = 100,
                                    onWinnerSelected,
                                    onSpinningChange,
                                    lotteriesWon = 0,
                                    maxLotteries = 3,
                                    isComplete = false,
                                }) => {
    const {trackRef, measureRef, cellHeight, strip, spinning, winnerValue, spin} = useSlotReel({
        pool: winnableLotteries,
        // Filler for when the draw is over and nothing is drawable — the marquee's
        // sample, which is already on screen beside the reel.
        idlePool: allLotteries,
        spinDuration,
        meritWinner,
        onSettled: onWinnerSelected,
    });

    // Surface the reel's spin state upward: the page needs it to lock out actions
    // that would swap the pool out from under a running animation. Pass a stable
    // setter (e.g. from useState) so this doesn't re-fire every render.
    useEffect(() => {
        onSpinningChange?.(spinning);
    }, [spinning, onSpinningChange]);

    // Stop the confetti canvas rather than letting it animate for the rest of the event.
    const [confettiOn, setConfettiOn] = useState(false);
    useEffect(() => {
        if (!winnerValue) return;
        setConfettiOn(true);
        const timer = setTimeout(() => setConfettiOn(false), CONFETTI_DURATION_MS);
        return () => clearTimeout(timer);
    }, [winnerValue]);

    const isDisabled = spinning || winnableLotteries.length === 0 || lotteriesWon >= maxLotteries || isComplete;

    return (
        <div className="w-full h-full relative">
            <div className="flex h-full gap-4">
                {/* ===== MAIN: One-reel slot ===== */}
                {/* Sizes are fluid rather than fixed: this runs on whatever screen the
                    event has — a projector or a short laptop — and a hard 600px card
                    clipped on both. (`md:` was also a no-op: tailwind.config.js
                    replaces `screens`, so sm/md/lg do not exist in this project.) */}
                <div className="flex-[8] flex items-center justify-center p-[clamp(0.75rem,2vw,2rem)]">
                    <div className="relative w-full max-w-6xl flex flex-col items-center justify-center h-[clamp(420px,74vh,780px)] rounded-3xl backdrop-blur-xl p-[clamp(1rem,2.5vw,2rem)] overflow-hidden"
                         style={{
                             background: 'var(--draw-surface)',
                             border: '1px solid var(--draw-line)',
                             boxShadow: '0 30px 80px rgba(0, 0, 0, 0.55)',
                         }}>

                        <div className="text-center mb-6">
                            {/* Solid gold, set inline. The old `bg-clip-text text-transparent`
                                pairing never worked: AntD's `.ant-typography` colour rule
                                outranks Tailwind's single-class `text-transparent`, so the
                                gradient was always hidden behind opaque dark glyphs — which
                                went unnoticed while the card was white. Inline beats both,
                                and a flat colour is what carries to the back of a room. */}
                            <Title
                                level={2}
                                className="mb-2"
                                style={{
                                    color: 'var(--draw-gold-bright)',
                                    textShadow: '0 2px 14px rgba(0, 0, 0, 0.7)',
                                }}
                            >
                                {name}
                            </Title>
                            <Space size="large" className="mb-4">
                                <Text className="text-base" style={{color: 'var(--draw-text-dim)'}}>
                                    {lotteriesWon} / {maxLotteries} азтан шалгарсан
                                </Text>
                            </Space>
                        </div>

                        <ReelWindow
                            trackRef={trackRef}
                            measureRef={measureRef}
                            cellHeight={cellHeight}
                            strip={strip}
                        />

                        <div className="flex flex-col items-center gap-4">
                            <Button
                                onClick={spin}
                                disabled={isDisabled}
                                size="large"
                                type="primary"
                                icon={spinning ? <PauseCircleOutlined/> : <PlayCircleOutlined/>}
                                className="draw-gold-button px-8 w-[200px] py-6 h-auto text-lg font-semibold rounded-full"
                                loading={spinning}
                            >
                                {spinning ? "Эргүүлж байна..." : isComplete ? "Дууссан" : "Эхлүүлэх"}
                            </Button>

                            {/* The region must exist in the DOM before it fills, or the
                                announcement is never read out. */}
                            <div role="status" aria-live="polite" className="text-center">
                                {winnerValue && !spinning && (
                                    <div className="animate-bounce-in">
                                        <div className="text-sm mb-1" style={{color: 'var(--draw-text-dim)'}}>
                                            <span aria-hidden="true">🎉 </span>
                                            Азтан болсон урамшууллын эрх
                                            <span aria-hidden="true"> 🎉</span>
                                        </div>
                                        <div className="text-3xl font-bold" style={{color: 'var(--draw-gold-bright)', textShadow: '0 0 24px rgba(232, 193, 112, 0.45)'}}>
                                            {winnerValue}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {isComplete && (
                                <div className="text-center p-4 rounded-lg" style={{background: 'var(--draw-ok-wash)', border: '1px solid rgba(111, 220, 160, 0.35)'}}>
                                    <Text className="font-medium" style={{color: 'var(--draw-ok)'}}>
                                        <span aria-hidden="true">🎊 </span>
                                        Урамшуулал амжилттай дууслаа!
                                        <span aria-hidden="true"> 🎊</span>
                                    </Text>
                                </div>
                            )}
                        </div>

                        {confettiOn && <ReactConfetti pieces={120} recycle={false}/>}

                        <div className="pointer-events-none absolute -top-20 -left-20 w-60 h-60 rounded-full bg-gradient-to-r from-[#E8C170]/20 to-[#A9762A]/10 blur-3xl"/>
                        <div className="pointer-events-none absolute -bottom-24 -right-24 w-72 h-72 rounded-full bg-gradient-to-r from-[#FFDD97]/18 to-[#A9762A]/12 blur-3xl"/>
                    </div>
                </div>

                {/* ===== SIDEBAR: Participants ===== */}
                <div className="flex-[2] h-full">
                    <ParticipantMarquee
                        lotteries={allLotteries}
                        winnerValue={winnerValue}
                        paused={spinning}
                        scrollSpeed={scrollSpeed}
                    />
                </div>
            </div>
        </div>
    );
};

RandomLotteryComponent.propTypes = {
    name: PropTypes.string,
    allLotteries: PropTypes.arrayOf(PropTypes.string),
    winnableLotteries: PropTypes.arrayOf(PropTypes.string),
    /** Employee merit winner's ticket, which this spin must land on; null for a draw by chance. */
    meritWinner: PropTypes.string,
    spinDuration: PropTypes.number,
    scrollSpeed: PropTypes.number,
    onWinnerSelected: PropTypes.func,
    onSpinningChange: PropTypes.func,
    lotteriesWon: PropTypes.number,
    maxLotteries: PropTypes.number,
    isComplete: PropTypes.bool,
};

export default memo(RandomLotteryComponent);
